import { useEffect, useState } from 'react';

interface Props {
  versionId: string;
  /** S3 key of the guide; undefined means none has been generated. */
  s3Key?: string;
}

export default function MigrationGuideContent({ versionId, s3Key }: Props) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!s3Key) {
      setContent(null);
      setError('no-guide');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    // The assets bucket is served by CloudFront at /assets/{s3Key}
    fetch(`/assets/${s3Key}`)
      .then(async (resp) => {
        if (!resp.ok) {
          setError('no-guide');
          return;
        }
        setContent(await resp.text());
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [s3Key]);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 24px', width: '100%' }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 8 }}>Migration Guide</h1>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0 }}>
          Version {versionId}
        </p>
      </div>

      {loading && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading migration guide…</p>
      )}

      {!loading && error === 'no-guide' && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          No migration guide is available for this version.
        </p>
      )}

      {!loading && error && error !== 'no-guide' && (
        <p
          style={{
            fontSize: '0.875rem',
            padding: '10px 14px',
            borderRadius: 6,
            background: 'var(--danger-bg)',
            color: 'var(--danger-text)',
            border: '1px solid var(--danger-border)',
          }}
        >
          {error}
        </p>
      )}

      {!loading && content && (
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'inherit',
            fontSize: '0.9rem',
            lineHeight: 1.7,
            margin: 0,
          }}
        >
          {content}
        </pre>
      )}
    </div>
  );
}
